import React, { memo, useContext, useState } from "react";
import { MeatballMenu } from "ui/common/dropdown-menu/meatball-menu";
import { routeConstants } from "app/routes/route-constants";
import { Link } from 'react-router-dom';
import { PromptConfirmationModal } from "ui/common/prompt-confirmation/prompt-confirmation-modal";
import { getHostingPath } from "app/infrastructure/hosting";
import { PrintLink } from "app/components/case-header-panel";
import { AddLossPotentialModal } from "app/area/case/components/loss-potential/add-loss-potential-evaluation-modal";
import { AddRiskEvaluationInitialValues } from "app/area/case/features/loss-potential-evaluation/types";
import { withGetUiDefinitionFromEntities } from "app/area/case/features/loss-potential-evaluation";
import { useSelectors } from "app/area/case-report/case-table-view/hooks/case-table-view-hooks";
import {
  ShareCase, SharePreset, UnlockCase, UnlockPreset, withSharingFromTextPath, withUnlockingFromTextPath
} from "app/area/case/features";
import { STATE } from "app/constants";
import { isValidUrl } from "app/infrastructure/validation/url";
import { DisplayLink, Link as ExternalLink } from 'ui';
import { urlToSurveyManager } from 'app/area/favourite/components/url-to-survey-manager';
import { ChangeCaseStatus } from "app/components/change-case-status/change-case-status";
import { AddNewConnectedCasesWireData } from "app/area/case/features/calls-to-actions/types";
import { UserType } from "app/types-shared";
import { AddNewConnectedCasesDropdown } from "app/components/add-new-cases-dropdown/add-new-connected-cases-dropdown";
import { useSharedState } from "app/infrastructure/redux/hooks";
import styles from "./case-table-view-meatball-menu.scss";
import { CaseTableViewContext } from "app/area/case-report/case-table-view/case-table-view-context";
import { getUrlOpenCase, getPathPrefix, shouldOpenCaseInModal, isCaseViewModalOpen } from "app/components/helpers";
import { tableName } from "app/area/case/case-data-model";
import * as routeUrls from "app/area/shared/route-urls";
import { Entity } from "app/types-shared";

interface CaseTableViewMeatballMenuProps {
  caseId: number;
  singleSupplierUrl?: string;
  row?: Entity[];
  params?: {id: string; dashboardType: string; favouriteId: string; bowtieCaseId: string};
}

// Find the action entity within THIS row only (a case can have multiple action rows,
// so we must not search across all rows for the case, only the one this menu belongs to)
function getEditActionEntityPath(row?: Entity[]) {
  const actionEntity = row?.find(entity => entity.id?.some(part => part[tableName.action] !== undefined));
  const actionIteratorNo = actionEntity?.id.find(part => part[tableName.action] !== undefined)?.[tableName.action];
  return actionIteratorNo ? `/${tableName.action}/${actionIteratorNo}` : '';
}

function getEntitiesForCaseId(entities: any, caseId: number) {
  return entities && entities.filter(entity => entity[0].CaseNo[0].value === caseId);
}

interface MenuUrlContext {
  caseId: number;
  paramsId: string | undefined;
  currentPath: string;
  searchString: string;
  editActionEntityPath: string;
}

const computeCaseEditUrl = (ctx: MenuUrlContext) => {
  if (!ctx.caseId) {
    return '';
  }
  if (shouldOpenCaseInModal(!ctx.paramsId)) {
    return `${getPathPrefix()}${ctx.currentPath}${routeConstants.caseEditFor(ctx.caseId)}${ctx.searchString}`;
  }
  return `${routeConstants.caseEditFor(ctx.caseId)}${ctx.searchString}`;
};

const computeCaseDuplicateUrl = (ctx: MenuUrlContext) => {
  if (!ctx.caseId) {
    return '';
  }
  if (isCaseViewModalOpen()) {
    return `${ctx.currentPath}${routeConstants.duplicate}${ctx.searchString}`;
  }
  if (shouldOpenCaseInModal(!ctx.paramsId)) {
    return `${getPathPrefix()}${ctx.currentPath}${routeConstants.caseDuplicateFor(ctx.caseId)}${ctx.searchString}`;
  }
  return `${routeConstants.caseDuplicateFor(ctx.caseId)}${ctx.searchString}`;
}

const computeEditActionUrl = (ctx: MenuUrlContext) => {
  if (!ctx.caseId) {
    return '';
  }
  if (!ctx.editActionEntityPath) {
    return '';
  }
  if (shouldOpenCaseInModal(!ctx.paramsId)) {
    return `${routeUrls.toCaseEditInModal(getPathPrefix() + ctx.currentPath, ctx.caseId, ctx.editActionEntityPath)}${ctx.searchString}`;
  }
  return `${routeUrls.toCaseEdit(ctx.caseId, ctx.editActionEntityPath)}${ctx.searchString}`;
};

const computeMenuUrls = (ctx: MenuUrlContext) => {
  return {
    caseURL: ctx.caseId ? routeConstants.caseViewFor(ctx.caseId) : "",
    caseEditUrl: computeCaseEditUrl(ctx),
    caseDuplicateUrl: computeCaseDuplicateUrl(ctx),
    caseBowtieUrl: ctx.caseId ? routeConstants.bowtieViewFor(ctx.caseId) : '',
    editActionUrl: computeEditActionUrl(ctx)
  };
};

const getCurrentPath = (location: { pathname: string }) => {
  return location?.pathname;
};

const computeSearchString = (searchParams: URLSearchParams) => {
  const searchParamsString = searchParams?.toString();
  return searchParamsString ? `?${searchParamsString}` : '';
};

const isAllowedForCurrentUser = (sharedState: any) => {
  return sharedState?.userType?.v !== UserType.basic;
};

const createOnExpandMenu = (
  getCallsToActions: ((caseId: number) => any) | undefined, caseId: number, setCallsToActions: (value: any) => void
) => {
  return async () => {
    const changeCaseStatusOptions = await getCallsToActions?.(caseId);
    setCallsToActions(changeCaseStatusOptions);
  };
};

const createCopyEvaluationHandler = (
  onCopyRiskEvaluation: ((caseId: number, riskMatrixInstance: number, instanceCopyCmd: number,
    initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => void) | undefined,
  caseId: number
) => {
  return (
    riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean
  ) => {
    onCopyRiskEvaluation?.(caseId, riskMatrixInstance, instanceCopyCmd, initialValues, copyToResidual);
  };
};

const EditActionMenuItem =(
  { callsToActions, editActionUrl, caseId, editActionEntityPath, text }:
  { callsToActions: any; editActionUrl: string; caseId: number; editActionEntityPath: string; text: string }
) => {
  if (!callsToActions?.editAccess || !editActionUrl) {
    return null;
  }
  return (
    <DisplayLink url={editActionUrl} urlHrefNewTabOverride={routeUrls.toCaseEdit(caseId, editActionEntityPath)}
      navigateToUrlForNewTab={true} className="btn dropdown-item" >
      {text}
    </DisplayLink>
  );
};

const EditCaseMenuItem = (
  { callsToActions, caseEditUrl, caseId, text }: { callsToActions: any; caseEditUrl: string; caseId: number; text: string }
) => {
  if (!callsToActions?.editAccess || !caseEditUrl) {
    return null;
  }
  return (
    <DisplayLink url={caseEditUrl} navigateToUrlForNewTab={true} urlHrefNewTabOverride={routeConstants.caseEditFor(caseId)}
      className="btn dropdown-item">{text}</DisplayLink>
  );
};

const AddLossPotentialMenuItem = (
  { allowForCurrentUser, callsToActions, entitiesForSpecificCaseId, menuProps, translation, onCopyRiskEvaluation }: {
    allowForCurrentUser: boolean; callsToActions: any; entitiesForSpecificCaseId: any;
    menuProps: CaseTableViewMeatballMenuProps; translation: any;
    onCopyRiskEvaluation: (riskMatrixInstance: number, instanceCopyCmd: number,
      initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => void;
  }
) => {
  if (!allowForCurrentUser) {
    return null;
  }
  if (!callsToActions?.lossPotentialSupported) {
    return null;
  }
  if (!entitiesForSpecificCaseId?.length) {
    return null;
  }
  const AddLossPotentialModalTableView = withGetUiDefinitionFromEntities(AddLossPotentialModal,
    Object.values(entitiesForSpecificCaseId[entitiesForSpecificCaseId.length - 1])[0]);

  return (
    <AddLossPotentialModalTableView {...menuProps}
      textValidFrom={translation.textEvaluationValidFrom}
      textEvaluationDescription={translation.textEvaluationDescription}
      textEvaluationHideInReport={translation.textEvaluationHideInReport}
      textAddEvaluationSubtitle={`${translation.textAddEvaluationSubtitle} ${' #'}${menuProps.caseId}`}
      textAddItem={translation.textAddEvaluationTitle}
      textAdd={translation.textEvaluationAddItem}
      textAddResidualEvaluation={translation.textAddResidualEvaluation}
      textEvaluationCopyFromPreviousResidual={translation.textEvaluationCopyFromPreviousResidual}
      addButtonStyle='dropdownItem'
      allowCopyFromOtherInstance={callsToActions?.residualLossPotential}
      allowCopyEvaluationToResidual={callsToActions?.residualLossPotential}
      onCopyRiskEvaluation={onCopyRiskEvaluation} />
  );
};

const AddNewConnectedCasesMenuItems = (
  { allowForCurrentUser, callsToActions, entitiesForSpecificCaseId, menuProps }: {
    allowForCurrentUser: boolean; callsToActions: any; entitiesForSpecificCaseId: any; menuProps: CaseTableViewMeatballMenuProps;
  }
) => {
  if (!allowForCurrentUser || !callsToActions?.addNewConnectedCases?.length) {
    return null;
  }
  return (
    <>
      {callsToActions.addNewConnectedCases.map((addNewConnectedCase: AddNewConnectedCasesWireData, index: number) => (
        <AddNewConnectedCasesDropdown
          entityId={menuProps.caseId}
          addNewConnectedCaseButtons={addNewConnectedCase.newCaseButtons}
          textLabelMenu={addNewConnectedCase.label}
          key={index}
          path={entitiesForSpecificCaseId[0]?.[0]?.id}
          {...menuProps} />
      ))}
    </>
  );
};

const ChangeCaseStatusMenuItem = (
  { allowForCurrentUser, callsToActions, caseId, caseEditUrl, changingCaseStatus, onChangeCaseStatus, translation }: {
    allowForCurrentUser: boolean; callsToActions: any; caseId: number; caseEditUrl: string;
    changingCaseStatus: boolean; onChangeCaseStatus: any; translation: any;
  }
) => {
  if (!callsToActions?.changeStatus) {
    return null;
  }
  if (!callsToActions?.editAccess) {
    return null;
  }
  if (!allowForCurrentUser) {
    return null;
  }
  return (
    <ChangeCaseStatus
      caseId={caseId}
      changeCaseStatusOptions={callsToActions.changeStatus}
      changeCaseStatus={onChangeCaseStatus}
      textChangeStatus={translation.textChangeStatus}
      textWarningModalTitle={translation.textChangeStatusWarningModalTitle}
      textWarningModalDescription={translation.textChangeStatusWarningModalDescription}
      textWarningModalContinue={translation.textChangeStatusWarningModalContinue}
      textChangeStatusSuccess={translation.textChangeStatusSuccess}
      textChangeStatusWithCaseId={translation.textChangeStatusWithCaseId}
      textChangeStatusForReload={translation.textChangeStatusForReload}
      textChangeStatusValidationError={translation.textChangeStatusValidationError}
      textWarningModalEditCase={translation.textChangeStatusWarningModalEditCase}
      reloadOnChangeStatus={false}
      allowWarnings={false}
      caseEditUrl={caseEditUrl}
      changingCaseStatus={changingCaseStatus}/>
  );
};

const OpenCaseMenuItem = (
  { caseURL, caseId, pathname, searchParams, text }:
  { caseURL: string; caseId: number; pathname: string; searchParams: URLSearchParams; text: string }
) => {
  if (!caseURL) {
    return null;
  }
  return (
    <DisplayLink value={caseId}
      url={getUrlOpenCase(caseId, pathname, searchParams)}
      className="btn dropdown-item">{text}
    </DisplayLink>
  );
};

const BowtieMenuItem = ({ callsToActions, caseBowtieUrl, text }: { callsToActions: any; caseBowtieUrl: string; text: string }) => {
  if (!callsToActions?.bowtieSupported) {
    return null;
  }
  return <Link to={caseBowtieUrl} className="btn dropdown-item" target="_self">{text}</Link>;
};  

const SurveyManagerMenuItem = (
  { callsToActions, access, singleSupplierUrl, caseId, text }:
  { callsToActions: any; access: any; singleSupplierUrl: string; caseId: number; text: string }
) => {
  const surveyManagerUrl = urlToSurveyManager(singleSupplierUrl, undefined, caseId);
  if (!callsToActions?.surveyManagerSupported) {
    return null;
  }
  if (!access?.surveyManager?.send) {
    return null;
  }
  if (!isValidUrl(surveyManagerUrl)) {
    return null;
  }
  return <ExternalLink url={surveyManagerUrl} linkText={text} className={'dropdown-item'} />;
};

const DuplicateCaseMenuItem = (
  { callsToActions, caseDuplicateUrl, caseId, text }: { callsToActions: any; caseDuplicateUrl: string; caseId: number; text: string }
) =>  {
  if (!callsToActions?.editAccess) {
    return null;
  }
  return (
    <DisplayLink url={caseDuplicateUrl} urlHrefNewTabOverride={routeConstants.caseDuplicateFor(caseId)}
      navigateToUrlForNewTab={true} className="btn dropdown-item" >
      {text}
    </DisplayLink>
  );
};

const ActionTriggerMenuItem = (
  { visible, Component, menuProps, text }:
  { visible: boolean; Component: any; menuProps: CaseTableViewMeatballMenuProps; text: string }
) =>  {
  if (!visible) {
    return null;
  }
  return (
    <Component {...menuProps}>
      {({ onOpen }) => <button type="button" className="dropdown-item" onClick={onOpen}>{text}</button>}
    </Component>
  );
}; 

const ShareCaseMenuItem = (
  { callsToActions, caseId, menuProps, text }:
  { callsToActions: any; caseId: number; menuProps: CaseTableViewMeatballMenuProps; text: string }
) => {
  const ShareCaseTableView: any = (withSharingFromTextPath(SharePreset.case, STATE.caseListing, caseId))(ShareCase);
  return (
    <ActionTriggerMenuItem visible={!!callsToActions?.editAccess} Component={ShareCaseTableView} menuProps={menuProps} text={text} />
  );
};

const DeleteCaseMenuItem = (
  { callsToActions, caseId, onDeleteCase, translation }:
  { callsToActions: any; caseId: number; onDeleteCase: (caseId: number) => void; translation: any }
) => {
  if (!callsToActions?.deleteAccess) {
    return null;
  }
  return (
    <PromptConfirmationModal
      title={translation.textDeleteCaseTitle}
      description={translation.textDeleteCaseDescription}
      continueText={translation.textDeleteCaseContinue}
      cancelText={translation.textDeleteCaseCancel}
      onContinue={() => onDeleteCase(caseId)}>
      <button type="button" className="dropdown-item">{translation.textDeleteCaseTitle}</button>
    </PromptConfirmationModal>
  );
};

const UnlockCaseMenuItem = (
  { callsToActions, caseId, menuProps, text }:
  { callsToActions: any; caseId: number; menuProps: CaseTableViewMeatballMenuProps; text: string }
) => {
  const UnlockCaseTableView: any = (withUnlockingFromTextPath(UnlockPreset.case, STATE.caseListing, caseId))(UnlockCase);
  return (
    <ActionTriggerMenuItem visible={!!callsToActions?.unlockAccess} Component={UnlockCaseTableView} menuProps={menuProps} text={text} />
  );
};

const CaseTableViewMeatballMenuMemo = (props: CaseTableViewMeatballMenuProps) =>  {
  const { caseId, singleSupplierUrl } = props;
  const { translation, onDeleteCase, tableViewRef, onCopyRiskEvaluation, callsToActions: contextCallsToActions,
    onChangeCaseStatus, location, searchParams, changingCaseStatus } = useContext(CaseTableViewContext);
  const [callsToActions, setCallsToActions] = useState(undefined);
  const { entities, access } = useSelectors();

  const sharedState = useSharedState();
  const paramsId = props.params?.id;
  const currentPath = getCurrentPath(location);
  const searchString = computeSearchString(searchParams);
  const allowForCurrentUser = isAllowedForCurrentUser(sharedState);

  const entitiesForSpecificCaseId = getEntitiesForCaseId(entities, caseId);
  const editActionEntityPath = getEditActionEntityPath(props.row);

  const { caseURL, caseEditUrl, caseDuplicateUrl, caseBowtieUrl, editActionUrl } = React.useMemo(
    () => computeMenuUrls({ caseId, paramsId, currentPath, searchString, editActionEntityPath }),
    [caseId, paramsId, editActionEntityPath, currentPath, searchString]
  );

  if (!caseId) {
    return null;
  }

  const onExpandMenu = createOnExpandMenu(contextCallsToActions, props.caseId, setCallsToActions);
  const handleCopyEvaluation = createCopyEvaluationHandler(onCopyRiskEvaluation, caseId);
  const portalTarget = tableViewRef?.current;

  return (
    <div className={styles.meatballMenuWrapper}>
      <MeatballMenu
        onExpandMenu={onExpandMenu}
        className={styles.meatballMenu}
        portalTarget={portalTarget}>
        <EditActionMenuItem callsToActions={callsToActions} editActionUrl={editActionUrl} caseId={caseId}
          editActionEntityPath={editActionEntityPath} text={translation.textEditActionTitle} />

        <EditCaseMenuItem callsToActions={callsToActions} caseEditUrl={caseEditUrl} caseId={caseId} text={translation.textEditCase} />

        <AddLossPotentialMenuItem allowForCurrentUser={allowForCurrentUser} callsToActions={callsToActions}
          entitiesForSpecificCaseId={entitiesForSpecificCaseId} menuProps={props} translation={translation}
          onCopyRiskEvaluation={handleCopyEvaluation} />

        <AddNewConnectedCasesMenuItems allowForCurrentUser={allowForCurrentUser} callsToActions={callsToActions}
          entitiesForSpecificCaseId={entitiesForSpecificCaseId} menuProps={props} />

        <ChangeCaseStatusMenuItem allowForCurrentUser={allowForCurrentUser} callsToActions={callsToActions} caseId={caseId}
          caseEditUrl={caseEditUrl} changingCaseStatus={changingCaseStatus} onChangeCaseStatus={onChangeCaseStatus}
          translation={translation} />

        <OpenCaseMenuItem caseURL={caseURL} caseId={caseId} pathname={currentPath} searchParams={searchParams}
          text={translation.textOpenCase} />

        <BowtieMenuItem callsToActions={callsToActions} caseBowtieUrl={caseBowtieUrl} text={translation.textOpenBowtieTitle} />

        <SurveyManagerMenuItem callsToActions={callsToActions} access={access} singleSupplierUrl={singleSupplierUrl} caseId={caseId}
          text={translation.textOpenInSurveyManager} />

        <DuplicateCaseMenuItem callsToActions={callsToActions} caseDuplicateUrl={caseDuplicateUrl} caseId={caseId}
          text={translation.textDuplicateCaseTitle} />

        <ShareCaseMenuItem callsToActions={callsToActions} caseId={caseId} menuProps={props} text={translation.textShareCase} />

        <PrintLink text={translation.textPrintCase} url={(getHostingPath() || '') + caseURL} />

        <DeleteCaseMenuItem callsToActions={callsToActions} caseId={caseId} onDeleteCase={onDeleteCase} translation={translation} />

        <UnlockCaseMenuItem callsToActions={callsToActions} caseId={caseId} menuProps={props} text={translation.textUnlockCase} />
      </MeatballMenu>
    </div>
  );
}
export const CaseTableViewMeatballMenu = memo(CaseTableViewMeatballMenuMemo);
